import { Link } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Copy, Check, BookOpen, Code2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useLang } from "@/i18n";

const ENDPOINTS = [
  { method: "GET", path: "/news", desc_tr: "Loft Press tarafından oluşturulan ve editoryal olarak onaylanan içerikleri listeler. Geriye dönük uyumluluk için endpoint adı korunmuştur; üçüncü taraf yayıncı akışları veya haber içerikleri döndürmez.", desc_en: "Lists content generated by Loft Press and approved by editorial review. Endpoint name is preserved for backward compatibility; does not return third-party publisher feeds or content.", params: "lang, category, limit, skip, search" },
  { method: "GET", path: "/news/trending", desc_tr: "Son yayınlanan onaylı Loft Press içeriklerini getirir.", desc_en: "Returns recently published, approved Loft Press content.", params: "lang, limit" },
  { method: "GET", path: "/news/{id}", desc_tr: "Tek bir onaylı içeriği kimliğine göre getirir.", desc_en: "Retrieves a single approved item by id.", params: "id" },
  { method: "GET", path: "/categories", desc_tr: "Mevcut kategorileri listeler.", desc_en: "Lists available categories.", params: "—" },
  { method: "POST", path: "/webhooks", desc_tr: "(Pro) Yeni onaylı içerik yayınlandığında bildirim webhook'u kaydeder.", desc_en: "(Pro) Registers a webhook for newly published approved content.", params: "url, categories" },
];

const SAMPLES = {
  curl: `curl https://loftpress.co/api/news \\
  -H "Authorization: Bearer lp_pro_xxxxxxxxxxxxxxxx" \\
  -G -d "lang=tr" -d "category=technology" -d "limit=10"

# Example response item (paid API)
# {
#   "id": "cnt_20260722_da64ae04",
#   "title": "RAM nasıl çalışır",
#   "summary": "RAM temelleri",
#   "body": "...",
#   "category": "technology",
#   "language": "tr",
#   "content_type": "article",
#   "keywords": ["RAM","bellek"],
#   "published_at": "2026-07-22T16:22:41Z",
#   "editorial_status": "approved",
#   "origin": "loftpress_generated"
# }`,
  python: `import requests

resp = requests.get(
    "https://loftpress.co/api/news",
    headers={"Authorization": "Bearer lp_pro_xxxxxxxxxxxxxxxx"},
    params={"lang": "tr", "category": "technology", "limit": 10},
)
print(resp.json())`,
  javascript: `const res = await fetch(
  "https://loftpress.co/api/news?lang=tr&category=technology&limit=10",
  { headers: { Authorization: "Bearer lp_pro_xxxxxxxxxxxxxxxx" } }
);
const data = await res.json();
console.log(data);`,
};

export default function ApiDocs() {
  const { lang, t } = useLang();
  const [copied, setCopied] = useState("");

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    toast.success("Copied!");
    setTimeout(() => setCopied(""), 1500);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="docs-page">
      <div className="flex items-center gap-3 mb-2">
        <BookOpen className="text-red-600" />
        <span className="eyebrow text-red-600">API REFERENCE · v1</span>
      </div>
      <h1 className="headline text-5xl sm:text-6xl tracking-tight text-white" data-testid="docs-title">{t("docs_title")}</h1>
      <p className="mt-4 text-lg text-neutral-400 max-w-3xl">{t("docs_intro")}</p>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <aside className="lg:col-span-3">
          <nav className="lg:sticky lg:top-32 space-y-1">
            <a href="#base-url" className="block text-sm py-1.5 px-3 rounded-sm hover:bg-neutral-900 text-neutral-300">Base URL</a>
            <a href="#auth" className="block text-sm py-1.5 px-3 rounded-sm hover:bg-neutral-900 text-neutral-300">Authentication</a>
            <a href="#endpoints" className="block text-sm py-1.5 px-3 rounded-sm hover:bg-neutral-900 text-neutral-300">{t("endpoints")}</a>
            <a href="#examples" className="block text-sm py-1.5 px-3 rounded-sm hover:bg-neutral-900 text-neutral-300">{t("examples")}</a>
            <a href="#rate-limits" className="block text-sm py-1.5 px-3 rounded-sm hover:bg-neutral-900 text-neutral-300">Rate Limits</a>
            <a href="#errors" className="block text-sm py-1.5 px-3 rounded-sm hover:bg-neutral-900 text-neutral-300">Errors</a>
            <div className="mt-6 p-4 bg-neutral-950 border border-neutral-800 rounded-sm">
              <p className="text-xs uppercase tracking-wider text-neutral-500 font-bold mb-2">Need a key?</p>
              <Link to="/marketplace" className="inline-block w-full text-center bg-red-600 hover:bg-red-700 text-white text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-sm transition-colors" data-testid="docs-get-key-cta">
                {t("cta_get_api")}
              </Link>
            </div>
          </nav>
        </aside>

        <div className="lg:col-span-9 space-y-14">
          <section id="base-url">
            <h2 className="headline text-3xl mb-4 text-white">{t("base_url")}</h2>
            <div className="bg-neutral-950 border border-neutral-800 text-green-400 font-mono text-sm p-4 rounded-sm flex items-center justify-between">
              <code>https://loftpress.co/api</code>
              <button onClick={() => copy("https://loftpress.co/api", "base")} className="text-neutral-500 hover:text-white" data-testid="copy-base-url">
                {copied === "base" ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>
          </section>

          <section id="auth">
            <h2 className="headline text-3xl mb-4 text-white">Authentication</h2>
            <p className="text-neutral-300 leading-relaxed">
              {lang === "tr"
                ? "Tüm istekler `Authorization` başlığında Bearer token gerektirir. API anahtarınızı pazaryeri sayfasından oluşturabilirsiniz."
                : "All requests must include a Bearer token in the `Authorization` header. You can generate your API key from the marketplace page."}
            </p>
            <div className="mt-4 bg-neutral-950 border border-neutral-800 text-neutral-300 font-mono text-sm p-4 rounded-sm overflow-x-auto">
              <pre>{`Authorization: Bearer lp_pro_xxxxxxxxxxxxxxxx`}</pre>
            </div>
          </section>

          <section id="endpoints">
            <h2 className="headline text-3xl mb-4 text-white">{t("endpoints")}</h2>
            <div className="border border-neutral-800 rounded-sm divide-y divide-neutral-800 bg-neutral-950" data-testid="endpoints-list">
              {ENDPOINTS.map((ep) => (
                <div key={ep.path} className="p-5">
                  <div className="flex items-center gap-3">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm ${
                      ep.method === "GET" ? "bg-green-900/40 text-green-400 border border-green-800" : "bg-blue-900/40 text-blue-400 border border-blue-800"
                    }`}>{ep.method}</span>
                    <code className="font-mono text-sm font-semibold text-white">{ep.path}</code>
                  </div>
                  <p className="mt-2 text-sm text-neutral-400">{lang === "tr" ? ep.desc_tr : ep.desc_en}</p>
                  <p className="mt-1 text-xs font-mono text-neutral-500">params: {ep.params}</p>
                </div>
              ))}
            </div>
          </section>

          <section id="examples">
            <h2 className="headline text-3xl mb-4 text-white">{t("examples")}</h2>
            <Tabs defaultValue="curl" className="w-full" data-testid="examples-tabs">
              <TabsList className="bg-neutral-900 rounded-sm border border-neutral-800">
                <TabsTrigger value="curl" data-testid="tab-curl">cURL</TabsTrigger>
                <TabsTrigger value="python" data-testid="tab-python">Python</TabsTrigger>
                <TabsTrigger value="javascript" data-testid="tab-javascript">JavaScript</TabsTrigger>
              </TabsList>
              {Object.entries(SAMPLES).map(([key, code]) => (
                <TabsContent key={key} value={key}>
                  <div className="relative bg-neutral-950 border border-neutral-800 text-neutral-200 font-mono text-sm p-5 rounded-sm">
                    <button
                      onClick={() => copy(code, key)}
                      className="absolute top-3 right-3 text-neutral-500 hover:text-white"
                      data-testid={`copy-${key}`}
                    >
                      {copied === key ? <Check size={16} /> : <Copy size={16} />}
                    </button>
                    <pre className="whitespace-pre-wrap overflow-x-auto">{code}</pre>
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </section>

          <section id="rate-limits">
            <h2 className="headline text-3xl mb-4 text-white">Rate Limits</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-neutral-800 rounded-sm bg-neutral-950">
                <thead className="bg-neutral-900">
                  <tr>
                    <th className="text-left px-4 py-3 font-bold uppercase tracking-wider text-xs text-neutral-300">Plan</th>
                    <th className="text-left px-4 py-3 font-bold uppercase tracking-wider text-xs text-neutral-300">Requests / Month</th>
                    <th className="text-left px-4 py-3 font-bold uppercase tracking-wider text-xs text-neutral-300">Burst / Minute</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["Free", "1,000", "10"],
                    ["Pro", "100,000", "200"],
                    ["Enterprise", "Unlimited", "Custom"],
                  ].map(([p, r, b]) => (
                    <tr key={p} className="border-t border-neutral-800">
                      <td className="px-4 py-3 font-semibold text-white">{p}</td>
                      <td className="px-4 py-3 font-mono text-neutral-300">{r}</td>
                      <td className="px-4 py-3 font-mono text-neutral-300">{b}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section id="errors">
            <h2 className="headline text-3xl mb-4 text-white">Errors</h2>
            <div className="space-y-3">
              {[
                ["401", "Unauthorized — missing or invalid API key"],
                ["403", "Forbidden — plan doesn't include this endpoint"],
                ["404", "Not Found — news id does not exist"],
                ["429", "Too Many Requests — rate limit exceeded"],
                ["500", "Server Error — please retry"],
              ].map(([code, msg]) => (
                <div key={code} className="flex items-start gap-3">
                  <code className="font-mono text-sm font-bold bg-red-950/50 text-red-400 border border-red-900 px-2 py-1 rounded-sm">{code}</code>
                  <span className="text-sm text-neutral-300">{msg}</span>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
